import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { createLogger } from '@/lib/logger'
import {
  hasHandledDesktopHandoff,
  isDesktopAppKnownInstalled,
  isDesktopHandoffLandingPage,
  isDesktopHandoffPage,
  markDesktopHandoffHandled,
  PREFER_DESKTOP_APP_KEY,
} from '@/lib/utils/desktop-app-handoff'
import { isTauri } from '@/lib/utils/tauri'
import { ExternalLink, Globe, Loader2, Monitor } from 'lucide-react'

const logger = createLogger('components/DeepLinkInterceptor')

// Session storage key to skip one deep-link check after explicit launcher routing
const SKIP_NEXT_DEEP_LINK_KEY = 'scrambl_deep_link_skip_next'
const DEEP_LINK_SCHEME = 'scrambl'

// Deep link attempt timeout in milliseconds
const DEEP_LINK_TIMEOUT = 2000

interface DeepLinkInterceptorProps {
  children: ReactNode
}

type InterceptorState = 'checking' | 'attempting' | 'fallback' | 'continue'

export function DeepLinkInterceptor({ children }: DeepLinkInterceptorProps) {
  const [state, setState] = useState<InterceptorState>('checking')
  const [rememberChoice, setRememberChoice] = useState(false)
  const hasChecked = useRef(false)

  const setPreferDesktopApp = (prefer: boolean) => {
    localStorage.setItem(PREFER_DESKTOP_APP_KEY, String(prefer))
  }

  // Check if we should attempt deep link
  const shouldAttemptDeepLink = (): boolean => {
    // Don't run if already in Tauri app
    if (isTauri()) {
      logger.info('[DeepLinkInterceptor] Running in Tauri, skipping')
      return false
    }

    // Skip exactly one navigation when explicitly requested by /open launcher
    if (sessionStorage.getItem(SKIP_NEXT_DEEP_LINK_KEY) === 'true') {
      sessionStorage.removeItem(SKIP_NEXT_DEEP_LINK_KEY)
      markDesktopHandoffHandled()
      logger.info(
        '[DeepLinkInterceptor] Explicit skip-next flag found, skipping once',
      )
      return false
    }

    // The full-page interceptor and bottom prompt share this marker.
    if (hasHandledDesktopHandoff()) {
      logger.info('[DeepLinkInterceptor] Handoff already handled, skipping')
      return false
    }

    // Browsers cannot inspect custom protocol handlers. Only try when a saved
    // preference or prior successful launch confirms the desktop app.
    if (!isDesktopAppKnownInstalled()) {
      logger.info('[DeepLinkInterceptor] Desktop app not confirmed, skipping')
      return false
    }

    // Today and bare org URLs consume the one-time decision without opening
    // the native protocol. They are the normal landing experience.
    if (isDesktopHandoffLandingPage(window.location.pathname)) {
      markDesktopHandoffHandled()
      logger.info('[DeepLinkInterceptor] Landing route, skipping')
      return false
    }

    // Only direct openings of concrete organization pages are eligible.
    if (!isDesktopHandoffPage(window.location.pathname)) {
      logger.info('[DeepLinkInterceptor] Excluded route, skipping')
      return false
    }

    return true
  }

  // Attempt to open the deep link
  const attemptDeepLink = () => {
    const path = window.location.pathname + window.location.search
    logger.info('[DeepLinkInterceptor] Attempting deep link to:', path)
    setState('attempting')

    // Mark before launching so the bottom prompt cannot repeat this attempt.
    markDesktopHandoffHandled()

    // Attempt the deep link
    const deepLinkUrl = `${DEEP_LINK_SCHEME}:/${path}`
    window.location.href = deepLinkUrl

    // After timeout, always show fallback - if the app opened the user is already there,
    // and if they cancelled or the app isn't installed they need actionable options
    setTimeout(() => {
      logger.info('[DeepLinkInterceptor] Timeout reached, showing fallback')
      setState('fallback')
    }, DEEP_LINK_TIMEOUT)
  }

  // Continue to web app
  const continueToWeb = () => {
    if (rememberChoice) {
      setPreferDesktopApp(false)
    }
    setState('continue')
  }

  // Retry opening in the desktop app
  const openInDesktopApp = () => {
    if (rememberChoice) {
      setPreferDesktopApp(true)
    }
    const path = window.location.pathname + window.location.search
    const deepLinkUrl = `${DEEP_LINK_SCHEME}:/${path}`
    window.location.href = deepLinkUrl
  }

  // Check on mount
  useEffect(() => {
    if (hasChecked.current) return
    hasChecked.current = true

    if (shouldAttemptDeepLink()) {
      attemptDeepLink()
    } else {
      setState('continue')
    }
  }, [])

  // Loading state while checking
  if (state === 'checking') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Attempting deep link state
  if (state === 'attempting') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Monitor className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Opening Scrambl</CardTitle>
            <CardDescription>Launching the desktop app…</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm text-muted-foreground">
                Checking for desktop app
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              If the app doesn&apos;t open automatically:
            </p>
            <Button
              onClick={continueToWeb}
              variant="outline"
              className="w-full"
            >
              <Globe className="mr-2 h-4 w-4" />
              Continue in browser
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Fallback UI when deep link fails
  if (state === 'fallback') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <ExternalLink className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Open in Scrambl</CardTitle>
            <CardDescription>
              Choose how you&apos;d like to continue
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              onClick={openInDesktopApp}
              className="h-12 w-full justify-start gap-3"
              variant="outline"
            >
              <Monitor className="h-5 w-5" />
              <div className="flex flex-col items-start">
                <span className="font-medium">Open in Desktop App</span>
                <span className="text-xs text-muted-foreground">
                  Try again or download the app
                </span>
              </div>
            </Button>

            <Button
              onClick={continueToWeb}
              className="h-12 w-full justify-start gap-3"
              variant="outline"
            >
              <Globe className="h-5 w-5" />
              <div className="flex flex-col items-start">
                <span className="font-medium">Continue in Browser</span>
                <span className="text-xs text-muted-foreground">
                  Use the web version
                </span>
              </div>
            </Button>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="remember"
                checked={rememberChoice}
                onCheckedChange={checked => setRememberChoice(checked === true)}
              />
              <Label
                htmlFor="remember"
                className="cursor-pointer text-sm text-muted-foreground"
              >
                Remember my choice
              </Label>
            </div>

            <p className="pt-2 text-center text-xs text-muted-foreground">
              You can change this preference anytime in Settings &gt; My
              Preferences &gt; App
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Continue state - render children (the actual app)
  return <>{children}</>
}
